import type { OverdueSection as OverdueSectionType } from "#/types/report";
import { formatCurrency } from "#/utils/formatCurrency";
import { Badge, Button } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { exportOverdueItemsCsv } from "../utils/exportOverdueItemsCsv";
import CollapsibleItemTable from "./CollapsibleItemTable";
import ProductOverdueSummary from "./ProductOverdueSummary";
import ReportSection from "./ReportSection";
import SiteOverdueSummary from "./SiteOverdueSummary";

interface OverdueSectionProps {
  title: string;
  data: OverdueSectionType;
  noItemsMessage: string;
  csvFilenamePrefix: string;
  showDaysSinceLastScan?: boolean;
  tooltip?: ReactNode;
}

const OverdueSection = ({
  title,
  data,
  noItemsMessage,
  csvFilenamePrefix,
  showDaysSinceLastScan = true,
  tooltip,
}: OverdueSectionProps) => {
  const { t } = useTranslation();

  const handleExportCsv = useCallback(() => {
    exportOverdueItemsCsv({
      items: data.items,
      filenamePrefix: csvFilenamePrefix,
      includeScanAge: showDaysSinceLastScan,
      t,
    });
  }, [data.items, csvFilenamePrefix, showDaysSinceLastScan, t]);

  if (data.totalCount === 0) {
    return (
      <ReportSection title={title} tooltip={tooltip}>
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {noItemsMessage}
        </div>
      </ReportSection>
    );
  }

  return (
    <ReportSection
      title={title}
      tooltip={tooltip}
      rightSection={
        <div className="flex items-center gap-2">
          <Badge variant="light" color="red" size="sm">
            {data.totalCount} items
          </Badge>
          <Badge variant="light" color="orange" size="sm">
            {formatCurrency(data.totalEstimatedValue)}
          </Badge>
          <Button
            data-export-csv-button
            leftSection={<IconDownload size={14} />}
            variant="outline"
            size="xs"
            onClick={handleExportCsv}
          >
            {t("common.exportCsv")}
          </Button>
        </div>
      }
    >
      {(data.byProduct.length > 0 || data.bySite.length > 0) && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {data.byProduct.length > 0 && <ProductOverdueSummary products={data.byProduct} />}
          {data.bySite.length > 0 && <SiteOverdueSummary sites={data.bySite} />}
        </div>
      )}
      <CollapsibleItemTable items={data.items} showDaysSinceLastScan={showDaysSinceLastScan} />
    </ReportSection>
  );
};

export default OverdueSection;
